import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withKeys as _withKeys, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = { class: "truncate" }

import type { ReferenceElement } from 'reka-ui';
    import { computedWithControl } from '@vueuse/core';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxPortal,
        ComboboxRoot,
        useFilter,
    } from 'reka-ui';
    import { computed, ref, watch, nextTick } from 'vue';
    import {
        getAnchorRect,
        getSearchValue,
        getTrigger,
        getTriggerOffset,
        replaceValue,
    } from '@/utils/comments';
    import { ArrowUp, LoaderCircle } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { useKeepersEmployeeData } from '@/composables/useKeepersEmployeeData';
    import type { User } from '@/types';
    import axios from 'axios';
    import { useMutation, useQueryClient } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import QueryKey from '@/enums/QueryKey';

    type MentionedUser = {
        listValue: string;
        value: string;
        email: string;
    };

    
export default /*@__PURE__*/_defineComponent({
  __name: 'CommentsInput',
  props: {
    companyId: {},
    statusId: {}
  },
  emits: ["comment-added"],
  setup(__props: any, { emit: __emit }) {

    const { contains } = useFilter({ sensitivity: 'base' });

    const queryClient = useQueryClient();

    const props = __props;

    const { keepersEmployees } = useKeepersEmployeeData(props.companyId);

    const page = usePage<SharedData>();

    const currentUser = computed(() => page.props.auth.user);

    const users = computed(() =>
        keepersEmployees.value.map((user: User) => {
            return {
                listValue: user.name,
                value: `@${user.name}`,
                email: user.email,
            };
        }),
    );

    const value = ref('');
    const mentions = ref<string[]>([]);
    const isMentioning = ref(false);
    const searchValue = ref('');
    const open = ref(false);
    const textareaRef = ref<InstanceType<typeof ComboboxInput>>();

    const reference = computedWithControl(
        () => [searchValue.value, open.value],
        () =>
            ({
                getBoundingClientRect: () => {
                    if (textareaRef.value?.$el) {
                        const { x, y, height } = getAnchorRect(textareaRef.value?.$el);
                        return { x, y, height, top: y, left: x, width: 0 };
                    } else {
                        return null;
                    }
                },
            }) as ReferenceElement,
    );

    const filteredUsers = computed(() => {
        if (!isMentioning.value) return [];
        return users.value
            .filter(
                (user: MentionedUser) =>
                    contains(user.listValue, searchValue.value) &&
                    user.email !== currentUser.value.email &&
                    !mentions.value.includes(user.email),
            )
            .map((user: MentionedUser) => user.listValue);
    });

    watch(
        () => filteredUsers.value.length,
        () => {
            open.value = !!filteredUsers.value.length;
        },
    );

    function autoResizeTextarea() {
        const textarea = textareaRef.value?.$el as HTMLTextAreaElement | undefined;
        if (!textarea) return;

        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 192);
        textarea.style.height = `${newHeight}px`;
    }

    function syncMentionsWithText() {
        const mentionRegex = /@(\w+)(?:\s|$|[^\w])/g;
        const textMentions: string[] = [];
        const matches = Array.from(value.value.matchAll(mentionRegex));

        for (const match of matches) {
            const mentionName = match[1];
            const user = users.value.find((u: MentionedUser) => u.listValue === mentionName);
            if (user) {
                textMentions.push(user.email);
            }
        }

        mentions.value = textMentions;
    }

    function handleChange(ev: InputEvent) {
        const target = ev.target as HTMLTextAreaElement;
        const trigger = getTrigger(target);
        const search = getSearchValue(target);

        if (trigger === '@') {
            isMentioning.value = true;
            open.value = true;
        } else if (!search) {
            isMentioning.value = false;
            open.value = false;
        }

        value.value = target.value;
        searchValue.value = search;

        if (!isMentioning.value) open.value = false;

        // Sync mentions whenever text changes
        syncMentionsWithText();

        nextTick(() => {
            autoResizeTextarea();
        });
    }

    function handleSelect(ev: CustomEvent) {
        const textarea = textareaRef.value?.$el;
        if (!textarea) return;

        const offset = getTriggerOffset(textarea);
        const selectedUser = users.value.find(
            (user: MentionedUser) => user.listValue === ev.detail.value,
        );
        if (!selectedUser) return;

        ev.preventDefault();

        isMentioning.value = false;
        value.value = replaceValue(value.value, offset, searchValue.value, selectedUser.value);

        // Sync mentions after adding a new one
        syncMentionsWithText();

        const nextCaretOffset = offset + selectedUser.value.length + 1;

        // Set cursor position
        nextTick(() => {
            textarea.setSelectionRange(nextCaretOffset, nextCaretOffset);
            autoResizeTextarea();
        });
    }

    watch(value, () => {
        nextTick(() => {
            autoResizeTextarea();
        });
    });

    const emit = __emit;

    const { isPending, isError, error, isSuccess, mutate } = useMutation({
        mutationFn: (newComment: { body: string; mentions: string[]; company_id: number }) =>
            axios.post(route('v2.dashboard.comments.store', props.statusId), newComment),
        onSuccess: (data) => {
            queryClient.setQueryData(
                [QueryKey.COMPANY_COMMENTS, props.companyId],
                (oldData: Comment[]) => (oldData ? [...oldData, data.data] : oldData),
            );
            value.value = '';
            mentions.value = [];
            emit('comment-added');
        },
    });

    function addComment() {
        if (isPending.value) return;
        mutate({
            body: value.value,
            mentions: mentions.value,
            company_id: props.companyId,
        });
    }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ComboboxRoot), {
    open: open.value,
    "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((open).value = $event)),
    "ignore-filter": "",
    "reset-search-term-on-blur": false,
    class: "text-foreground flex flex-col"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(ComboboxInput), {
          id: "comment",
          ref_key: "textareaRef",
          ref: textareaRef,
          modelValue: value.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((value).value = $event)),
          as: "textarea",
          class: "border-input z-50 flex min-h-10 w-full min-w-0 resize-none overflow-x-hidden overflow-y-hidden rounded-md border bg-transparent py-2.5 pr-10 pl-2.5 text-xs shadow-xs transition-[color,box-shadow] outline-none",
          rows: "1",
          placeholder: _ctx.$t('dashboards.comment.placeholder'),
          onInput: handleChange,
          onPointerdown: _cache[1] || (_cache[1] = ($event: any) => (open.value = false)),
          onKeydown: [
            _cache[2] || (_cache[2] = _withKeys(
                    (ev) => {
                        if (open.value) ev.preventDefault();
                    }
                , ["enter"])),
            _cache[3] || (_cache[3] = _withKeys(($event: any) => (open.value = false), ["left","right"]))
          ]
        }, null, 8, ["modelValue", "placeholder"]),
        _createVNode(_unref(Button), {
          variant: "secondary",
          size: "icon",
          class: "absolute top-2.5 right-2.5 size-5",
          onClick: addComment,
          disabled: !value.value || _unref(isPending)
        }, {
          default: _withCtx(() => [
            (!_unref(isPending))
              ? (_openBlock(), _createBlock(_unref(ArrowUp), {
                  key: 0,
                  class: "text-white"
                }))
              : (_openBlock(), _createBlock(_unref(LoaderCircle), {
                  key: 1,
                  class: "size-3 animate-spin text-white"
                }))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      _createVNode(_unref(ComboboxAnchor), { reference: _unref(reference) }, null, 8, ["reference"]),
      _createVNode(_unref(ComboboxPortal), null, {
        default: _withCtx(() => [
          (filteredUsers.value.length)
            ? (_openBlock(), _createBlock(_unref(ComboboxContent), {
                key: 0,
                position: "popper",
                side: "bottom",
                align: "start",
                class: "z-50 max-h-48 max-w-80 overflow-x-hidden overflow-y-auto rounded-md border bg-white p-1.5 shadow-md"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredUsers.value, (user) => {
                    return (_openBlock(), _createBlock(_unref(ComboboxItem), {
                      key: user,
                      value: user,
                      class: "data-[highlighted]:bg-primary/10 data-[highlighted]:text-primary flex cursor-default rounded-sm px-2 py-1 text-xs",
                      onSelect: handleSelect
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", _hoisted_2, _toDisplayString(user), 1)
                      ]),
                      _: 2
                    }, 1032, ["value"]))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})